<template>
    <div v-show="isExpanded" class="summary-header volume-funding__header">
        <div class="summary-header__title">
            <label class="summary-header__label">
                {{ params.displayName }}
            </label>
        </div>
        <div class="summary-header__info">
            {{ formattedValue }}
        </div>
        <template v-if="hasOverwriteVolumes">
            <div class="summary-header__reason-title">
                <span>
                    {{
                        toSentenceCase(
                            $t('planning.promotionsMaintenance.funding.overWriteReason')
                        )
                    }}:
                </span>
            </div>
            <div class="summary-header__container">
                <v-tooltip z-index="400" top :max-width="200" :disabled="!toolTipValue">
                    <template v-slot:activator="{ props }">
                        <div v-bind="props">
                            <vuex-select
                                class="summary-header__reason"
                                item-text="text"
                                :options="overwriteReasonOptions"
                                vuex-module="promotions"
                                :field-name="overWriteReasonFieldName"
                                filled
                                :namespace="params.promotionId"
                                :validations="validationRules"
                                :validate-on-blur="false"
                                :ignore-parent-read-only="overwriteReasonReadOnlyIgnore"
                                force-validate-on-mount
                                tab="funding"
                                :disabled="isReadOnly"
                            />
                        </div>
                    </template>
                    <span>
                        {{ toolTipValue }}
                    </span>
                </v-tooltip>
            </div>
        </template>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import { get, isNil } from 'lodash';
import { mapGetters, mapState, mapActions } from 'vuex';
import validators from '@/js/validators';
import fundingUtils from '@sharedModules/funding-utils';

const { getIgnoreReadOnlyFlag } = fundingUtils;

export default defineComponent({
    data() {
        return {
            isExpanded: true,
            overrideSumFieldNames: ['uplift', 'baseline'],
            overWriteReasonFieldName: 'volumesOverwriteReason',
        };
    },
    computed: {
        ...mapGetters('promotions', ['getStagingAreaPromotionById', 'selectedPromotion']),
        ...mapState('clientConfig', ['toggleLogic', 'volumeOverwriteReasons']),
        formattedValue() {
            return this.totalMargin(this.totalPromotionFunding);
        },
        toolTipValue() {
            const selectedOption = this.overwriteReasonOptions.find(
                option => option.value === this.promotion[this.overWriteReasonFieldName]
            );
            if (selectedOption) {
                return selectedOption.text;
            }

            return null;
        },
        validationRules() {
            return [
                {
                    validator: validators.required,
                },
            ];
        },
        hasOverwriteVolumes() {
            // volumes are overwriten if any of the
            // overrideSumFieldNames values in any product is overwriten (not null)
            return this.promotion.products?.reduce((result, product) => {
                const isOverridden = this.overrideSumFieldNames.some(overrideFieldName => {
                    return !isNil(get(product, `volumes.${overrideFieldName}`));
                });
                return result || isOverridden;
            }, false);
        },
        totalPromotionFunding() {
            const { products } = this.promotion;

            // Total promotion funding is the sum of the lump funding
            // and variable funding across each product.
            return products.reduce((fundingSum, product) => {
                const lumpFunding = get(product, 'funding.lumpFundingTotal', 0);
                const volume = get(product, 'volumes.totalVolume', 0);
                const unitFundingValue = get(
                    product,
                    'funding.variableFunding.unitFundingValue',
                    0
                );
                const unitFunding = volume * unitFundingValue;
                fundingSum += lumpFunding + unitFunding;
                return fundingSum;
            }, 0);
        },
        promotion() {
            return this.getStagingAreaPromotionById(this.params.promotionId);
        },
        overwriteReasonOptions() {
            return this.volumeOverwriteReasons.map(option => ({
                text: option.text,
                value: Number(option.value),
            }));
        },
        overwriteReasonReadOnlyIgnore() {
            return getIgnoreReadOnlyFlag({
                selectedPromotion: this.selectedPromotion,
                toggleLogic: this.toggleLogic,
                promotionEditingState: this.params.promotionEditingState,
            });
        },
        isReadOnly() {
            return this.params.context?.isReadOnly() && !this.overwriteReasonReadOnlyIgnore;
        },
    },
    watch: {
        hasOverwriteVolumes(value) {
            if (!value) {
                this.setStagingAreaField({
                    namespace: this.params.promotionId,
                    fieldName: this.overWriteReasonFieldName,
                    value: null,
                });
            }
        },
    },
    events: {
        onVolumeFundingCollapsed(state) {
            this.isExpanded = state;
            this.params.setExpanded(this.isExpanded);
        },
    },
    methods: {
        ...mapActions('promotions', ['setStagingAreaField']),
    },
});
</script>

<style lang="scss">
@import '@style/base/_variables.scss';

.summary-header.volume-funding__header {
    flex-grow: 1;
    height: 100%;
    display: flex;
    padding: 0 0 0 1rem;

    flex-direction: column;
    justify-content: space-between;

    .summary-header__title {
        padding: 0;
        display: flex;
        justify-content: space-between;
    }

    .summary-header__label {
        white-space: normal;
        min-height: 3.4rem;
    }

    .summary-header__info {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: flex-start;
        background-color: $promo-table-blue-bg-colour-3;
        padding: 0.5rem;
        width: 100%;
    }

    .summary-header__container {
        max-width: 40rem;
        border-top: 0.1rem solid #c2c2c2;
    }

    .summary-header__reason {
        margin-top: 0.5rem;
        max-width: 20rem;
    }

    .summary-header__reason-title {
        margin-top: 0.4rem;
    }

    .expand-btn {
        width: 1.8rem;
        height: 1.8rem;
        margin-right: 0.5rem;
    }
}
</style>
